import React from 'react';
import { NodeViewProps } from '@tiptap/core';
import { NodeViewWrapper } from '@tiptap/react';
import ProjectEmbedCard from '../projectEmbedCard';
import createEmbed from './createEmbed';

export interface ProjectLinkEmbedOptions {
  HTMLAttributes?: Record<string, string>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    projectLinkEmbed: {
      setProjectLinkEmbed: (options: {
        url: string;
        projectId: string;
        projectName: string;
        themeMode?: string;
        projectStatusText?: string;
        projectStatusColor?: string;
      }) => ReturnType;
      removeProjectLinkEmbed: () => ReturnType;
    };
  }
}

// React NodeView Component using ProjectEmbedCard
const ProjectLinkEmbedComponent: React.FC<NodeViewProps> = ({ node }) => {
  const { url, projectId, projectName, projectStatusText } = node.attrs;

  // Create a node object matching the expected format for ProjectEmbedCard
  const projectNode = {
    attribs: {
      url: url,
      projectid: projectId,
      projectname: projectName,
      projectstatus: JSON.stringify({
        statusText: projectStatusText,
        status: 1, // Default status
        progress: 0 // Default progress
      })
    }
  };

  return (
    <NodeViewWrapper className='project-link-embed-wrapper'>
      <ProjectEmbedCard node={projectNode} />
    </NodeViewWrapper>
  );
};

export const ProjectLinkEmbed = createEmbed({
  name: 'projectLinkEmbed',
  tag: 'div.project-link-embed-preview-main-div',
  component: ProjectLinkEmbedComponent,
  selectable: true,
  draggable: false,
  attributes: {
    url: { default: '' },
    projectId: { default: '' },
    projectName: { default: '' },
    themeMode: { default: 'light' },
    projectStatusColor: { default: 'var(--neutral-primary)' },
    projectStatusText: { default: 'Not Started yet (0%)' }
  },
  parseHTMLGetAttrs: (dom: HTMLElement) => {
    return {
      url: dom.getAttribute('url'),
      projectId: dom.getAttribute('projectId'),
      projectName: dom.getAttribute('projectName'),
      themeMode: dom.getAttribute('themeMode'),
      projectStatusColor: dom.getAttribute('projectStatusColor'),
      projectStatusText: dom.getAttribute('projectStatusText')
    };
  },
  renderHTMLAttributes: (HTMLAttributes) => {
    return {
      class: 'project-link-embed-preview-main-div',
      url: HTMLAttributes?.url,
      projectId: HTMLAttributes?.projectId,
      projectName: HTMLAttributes?.projectName,
      projectStatusColor: HTMLAttributes?.projectStatusColor,
      projectStatusText: HTMLAttributes?.projectStatusText,
      themeMode: HTMLAttributes?.themeMode
    };
  },
  extraCommands: {
    removeProjectLinkEmbed:
      (embedName: string) =>
      ({
        commands,
        state
      }: {
        commands: { deleteNode: (name: string) => boolean };
        state: { selection: { $from: { node: () => { type: { name: string } } } } };
      }) => {
        const { selection } = state;
        const { $from } = selection;
        const node = $from.node();

        if (node.type.name === embedName) {
          return commands.deleteNode(embedName);
        }
        return false;
      }
  }
});
